/**
 * Skeleton.base — Primitiva base del sistema de pantalla esqueleto.
 *
 * QUÉ HACE: provee el bloque shimmer atómico (rect | circle | text-line) que
 *   usan todos los presets de `Skeleton.presets.tsx`. El efecto "shimmer" usa
 *   las variables CSS --sk-base y --sk-sheen definidas en index.css.
 * QUÉ INVOCA: nada externo (React + Tailwind).
 * QUÉ EXPORTA: Skeleton.
 * QUÉ NO DEBE HACER: no contiene lógica de negocio, datos ni presets compuestos.
 */

import React from "react";

/* ═══════════════════════════════════════════════════════════════
   PRIMITIVA BASE
   ═══════════════════════════════════════════════════════════════ */

interface SkeletonProps {
  /** Ancho (clase Tailwind o valor CSS). Por defecto "w-full". */
  width?: string;
  /** Alto  (clase Tailwind o valor CSS). Por defecto "h-4". */
  height?: string;
  /**
   * Redondeo:
   *  "sm"   → rounded
   *  "md"   → rounded-md
   *  "lg"   → rounded-lg
   *  "xl"   → rounded-xl
   *  "full" → rounded-full  (ideal para círculos/badges)
   */
  rounded?: "sm" | "md" | "lg" | "xl" | "full";
  /** Clases CSS adicionales */
  className?: string;
}

const roundedMap: Record<NonNullable<SkeletonProps["rounded"]>, string> = {
  sm: "rounded",
  md: "rounded-md",
  lg: "rounded-lg",
  xl: "rounded-xl",
  full: "rounded-full",
};

/**
 * Bloque shimmer atómico. Úsalo como building-block de presets o
 * directamente cuando necesites un placeholder un-off.
 *
 * @example
 * <Skeleton width="w-32" height="h-4" rounded="md" />
 * <Skeleton width="w-10" height="h-10" rounded="full" />  // avatar circle
 */
export const Skeleton: React.FC<SkeletonProps> = ({
  width = "w-full",
  height = "h-4",
  rounded = "md",
  className = "",
}) => (
  <div
    aria-hidden="true"
    className={`skeleton ${width} ${height} ${roundedMap[rounded]} ${className}`}
  />
);
